// Finanzen: Übersicht, Buchungen, Konten und Darlehen.
//
// Die Übersicht ist der Kern. Alles andere ist Erfassung — sie ist das, wofür
// man die Erfassung macht.

import { useEffect, useState } from "react";
import { api, ApiFehler } from "../api.ts";
import { anzahl, eur, eur2, pct, datum as alsDatum, heute } from "../formate.ts";
import {
  Kpi, Mini, Leer, Sheet, TextFeld, ZahlFeld, WahlFeld, HakenFeld,
  TextflaecheFeld, ausRecord,
} from "../komponenten/Bausteine.tsx";
import { useBestand, useZustand } from "../zustand.tsx";
import {
  KONTOARTEN, TYP_TEXT,
  type Buchung, type Darlehen, type Jahreszahlen, type KategorieSumme,
  type Konto, type Objekt, type Portfolio,
} from "../typen.ts";

// ---------------------------------------------------------------- Hilfsmittel

const bezugOptionen = (objekte: Objekt[]): (readonly [string, string])[] => {
  const liste: (readonly [string, string])[] = [["", "Portfolioweit (kein Bezug)"] as const];
  for (const o of objekte) {
    liste.push([`objekt:${o.id}`, o.name] as const);
    for (const e of o.einheiten) liste.push([`einheit:${e.id}`, `${o.name} — ${e.bezeichnung}`] as const);
  }
  return liste;
};
const bezugAusFeldern = (objektId: string | null, einheitId: string | null): string =>
  einheitId ? `einheit:${einheitId}` : objektId ? `objekt:${objektId}` : "";
const bezugZuFeldern = (wert: string) => ({
  objekt_id: wert.startsWith("objekt:") ? wert.slice(7) : null,
  einheit_id: wert.startsWith("einheit:") ? wert.slice(8) : null,
});
const bezugName = (objekte: Objekt[], objektId: string | null, einheitId: string | null): string => {
  if (einheitId) {
    for (const o of objekte) {
      const e = o.einheiten.find((x) => x.id === einheitId);
      if (e) return `${o.name} · ${e.bezeichnung}`;
    }
  }
  if (objektId) return objekte.find((o) => o.id === objektId)?.name ?? "";
  return "portfolioweit";
};

/** Vorzeichenbehaftete Darstellung: Einnahmen grün, Ausgaben rot. */
function Betrag({ wert, art }: { wert: number; art: "einnahme" | "ausgabe" }) {
  return (
    <span style={{
      fontWeight: 700, fontVariantNumeric: "tabular-nums",
      color: art === "einnahme" ? "var(--green-ink)" : "var(--red)",
    }}>
      {art === "einnahme" ? "+" : "−"}{eur2(wert)}
    </span>
  );
}

const reiter = [
  ["uebersicht", "Übersicht"], ["buchungen", "Buchungen"],
  ["konten", "Konten"], ["darlehen", "Darlehen"],
] as const;

// ------------------------------------------------------------------ Buchungen

function BuchungBlatt(
  { buchung, aufSchliessen }: { buchung: Buchung | null; aufSchliessen: () => void },
) {
  const bestand = useBestand();
  const { aendern } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);
  const [f, setzeF] = useState({
    datum: buchung?.datum ?? heute(),
    betrag: buchung ? String(buchung.betrag) : "",
    kategorie_id: buchung?.kategorie_id ?? "",
    konto_id: buchung?.konto_id ?? "",
    beschreibung: buchung?.beschreibung ?? "",
    art: buchung?.art ?? "ausgabe",
    typ: buchung?.typ ?? "laufend",
    absetzbar: buchung?.absetzbar ?? true,
    umlagefaehig: buchung?.umlagefaehig ?? false,
    bezug: bezugAusFeldern(buchung?.objekt_id ?? null, buchung?.einheit_id ?? null),
  });
  const setze = <K extends keyof typeof f>(k: K) => (w: typeof f[K]) => setzeF({ ...f, [k]: w });

  /** Kategoriewechsel belegt die steuerlichen Merkmale neu vor — sie bleiben
   *  danach von Hand änderbar, weil der Einzelfall entscheidet. */
  const setzeKategorie = (id: string) => {
    const k = bestand.kategorien.find((x) => x.id === id);
    setzeF(k
      ? { ...f, kategorie_id: id, art: k.art, typ: k.typ, absetzbar: k.absetzbar, umlagefaehig: k.umlagefaehig }
      : { ...f, kategorie_id: id });
  };

  const zahl = (w: string) => {
    const n = Number(w.replace(",", "."));
    return Number.isFinite(n) ? Math.abs(n) : 0;
  };

  const kategorieOptionen = [
    ["", "— keine —"] as const,
    ...bestand.kategorien
      .filter((k) => k.art === f.art)
      .map((k) => [k.id, k.name] as const),
  ];

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (zahl(f.betrag) <= 0) return;
      const daten = {
        datum: f.datum, betrag: zahl(f.betrag), art: f.art,
        kategorie_id: f.kategorie_id || null, konto_id: f.konto_id || null,
        beschreibung: f.beschreibung, typ: f.typ,
        absetzbar: f.absetzbar, umlagefaehig: f.umlagefaehig,
        ...bezugZuFeldern(f.bezug),
      };
      setzeLaeuft(true);
      const ok = await aendern(
        () => buchung ? api.buchungAendern(buchung.id, daten) : api.buchungAnlegen(daten),
        "Gespeichert");
      setzeLaeuft(false);
      if (ok) aufSchliessen();
    }}>
      <Sheet
        titel={buchung ? "Buchung bearbeiten" : "Neue Buchung"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {buchung ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm("Diese Buchung löschen?")) return;
              void aendern(() => api.buchungLoeschen(buchung.id), "Gelöscht").then(aufSchliessen);
            }}>Löschen</button>
          ) : <span />}
          <div className="r">
            <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
            <button className="btn btn-primary" type="submit" disabled={laeuft}>
              {laeuft ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>}
      >
        <div className="form-grid">
          <WahlFeld titel="Richtung" wert={f.art} aendern={(w) =>
            setzeF({ ...f, art: w, kategorie_id: "" })}
            optionen={[["ausgabe", "Ausgabe"], ["einnahme", "Einnahme"]] as const} />
          <ZahlFeld titel="Betrag (€) *" wert={f.betrag} aendern={setze("betrag")} platzhalter="0,00" />
          <WahlFeld titel="Kategorie" voll wert={f.kategorie_id} aendern={setzeKategorie}
            optionen={kategorieOptionen} />
          <TextFeld titel="Datum" typ="date" wert={f.datum} aendern={setze("datum")} />
          <WahlFeld titel="Konto" wert={f.konto_id} aendern={setze("konto_id")}
            optionen={[["", "— kein Konto —"] as const,
              ...bestand.konten.map((k) => [k.id, k.name] as const)]} />
          <TextFeld titel="Beschreibung" voll wert={f.beschreibung} aendern={setze("beschreibung")}
            platzhalter="z. B. Rechnung Heizung Müller" />
          <WahlFeld titel="Gehört zu" voll wert={f.bezug} aendern={setze("bezug")}
            optionen={bezugOptionen(bestand.objekte)} />
          <WahlFeld titel="Art der Kosten" wert={f.typ} aendern={setze("typ")}
            optionen={ausRecord(TYP_TEXT)} />
          <HakenFeld titel="Steuerlich absetzbar" wert={f.absetzbar} aendern={setze("absetzbar")} />
          <HakenFeld titel="Umlagefähig auf Mieter" wert={f.umlagefaehig}
            aendern={setze("umlagefaehig")} />
        </div>
        <p className="hint">
          Kategorie belegt „Art der Kosten“, „absetzbar“ und „umlagefähig“ vor — der
          Einzelfall entscheidet aber selbst. Dieselbe Handwerkerrechnung ist mal
          Erhaltungsaufwand (sofort absetzbar), mal nachträgliche Herstellungskost
          (nur über die AfA).
        </p>
      </Sheet>
    </form>
  );
}

function Buchungen() {
  const bestand = useBestand();
  const [blatt, setzeBlatt] = useState<{ buchung: Buchung | null } | null>(null);
  const [richtung, setzeRichtung] = useState<"alle" | "einnahme" | "ausgabe">("alle");
  const [kontoFilter, setzeKontoFilter] = useState("alle");

  const gefiltert = bestand.buchungen.filter((b) =>
    (richtung === "alle" || b.art === richtung)
    && (kontoFilter === "alle" || b.konto_id === kontoFilter));

  const summe = (art: "einnahme" | "ausgabe") =>
    gefiltert.filter((b) => b.art === art).reduce((s, b) => s + b.betrag, 0);

  return (
    <>
      <div className="san-head">
        <div className="seg" style={{ margin: 0 }}>
          {([["alle", "Alle"], ["einnahme", "Einnahmen"], ["ausgabe", "Ausgaben"]] as const)
            .map(([k, t]) => (
              <button key={k} className={richtung === k ? "on" : ""}
                onClick={() => setzeRichtung(k)}>{t}</button>
            ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {bestand.konten.length > 1 ? (
            <select value={kontoFilter} onChange={(e) => setzeKontoFilter(e.target.value)}
              style={{
                border: "1px solid var(--line)", borderRadius: 10, padding: "7px 10px",
                background: "#FBFBFD", fontSize: 13,
              }}>
              <option value="alle">Alle Konten</option>
              {bestand.konten.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          ) : null}
          <span className="sub" style={{ margin: 0 }}>
            {anzahl(gefiltert.length, "Buchung", "Buchungen")} · +{eur(summe("einnahme"))} / −{eur(summe("ausgabe"))}
          </span>
          <button className="btn btn-primary btn-sm"
            onClick={() => setzeBlatt({ buchung: null })}>+ Buchung</button>
        </div>
      </div>

      {gefiltert.length === 0 ? (
        <Leer titel="Keine Buchungen.">
          Mieteingänge, Rechnungen und Darlehensraten erfassen — daraus entstehen
          Kontostand, Jahresübersicht und die Zahlen für die Steuerberatung.
        </Leer>
      ) : (
        <div className="table">
          <div className="trow head buchung">
            <span className="hide-m">Datum</span><span>Beschreibung</span>
            <span className="hide-m">Kategorie</span><span className="num">Betrag</span><span />
          </div>
          {gefiltert.map((b) => (
            <div className="trow buchung" key={b.id}>
              <span className="sub hide-m" style={{ margin: 0, whiteSpace: "nowrap" }}>
                {alsDatum(b.datum)}
              </span>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {b.beschreibung || b.kategorie_name || "Ohne Beschreibung"}
                </div>
                <div className="row-sub">
                  <span className="nur-m">{alsDatum(b.datum)} · </span>
                  {bezugName(bestand.objekte, b.objekt_id, b.einheit_id)}
                  {b.konto_name ? ` · ${b.konto_name}` : ""}
                  {b.typ !== "laufend" ? ` · ${TYP_TEXT[b.typ]}` : ""}
                  {!b.absetzbar ? " · nicht absetzbar" : ""}
                  {b.umlagefaehig ? " · umlagefähig" : ""}
                </div>
              </div>
              <span className="hide-m sub" style={{ margin: 0, fontSize: 13 }}>
                {b.kategorie_name ?? "—"}
              </span>
              <span className="num"><Betrag wert={b.betrag} art={b.art} /></span>
              <span>
                <button className="btn-xs" onClick={() => setzeBlatt({ buchung: b })}>Bearb.</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {blatt ? <BuchungBlatt buchung={blatt.buchung} aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}

// --------------------------------------------------------------------- Konten

function KontoBlatt(
  { konto, aufSchliessen }: { konto: Konto | null; aufSchliessen: () => void },
) {
  const { aendern, zeigeMeldung } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);
  const [f, setzeF] = useState({
    name: konto?.name ?? "", art: konto?.art ?? "giro", bank: konto?.bank ?? "",
    iban: "", startsaldo: konto ? String(konto.startsaldo) : "", startsaldo_am: "",
    aktiv: konto?.aktiv ?? true, notiz: "",
  });
  const setze = <K extends keyof typeof f>(k: K) => (w: typeof f[K]) => setzeF({ ...f, [k]: w });

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (!f.name.trim()) return;
      const zahl = Number(f.startsaldo.replace(",", "."));
      const daten = {
        name: f.name.trim(), art: f.art, bank: f.bank, iban: f.iban,
        startsaldo: Number.isFinite(zahl) ? zahl : 0,
        startsaldo_am: f.startsaldo_am || null, aktiv: f.aktiv, notiz: f.notiz,
      };
      setzeLaeuft(true);
      const ok = await aendern(
        () => konto ? api.bankkontoAendern(konto.id, daten) : api.bankkontoAnlegen(daten),
        "Gespeichert");
      setzeLaeuft(false);
      if (ok) aufSchliessen();
    }}>
      <Sheet
        titel={konto ? "Konto bearbeiten" : "Neues Konto"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {konto ? (
            <button className="btn btn-danger" type="button" onClick={async () => {
              if (!confirm(`Konto „${konto.name}“ löschen?`)) return;
              try {
                await api.bankkontoLoeschen(konto.id);
                zeigeMeldung("Konto gelöscht");
                aufSchliessen();
              } catch (fehler) {
                zeigeMeldung(fehler instanceof ApiFehler ? fehler.message : "Nicht möglich.", true);
              }
            }}>Löschen</button>
          ) : <span />}
          <div className="r">
            <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
            <button className="btn btn-primary" type="submit" disabled={laeuft}>
              {laeuft ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>}
      >
        <div className="form-grid">
          <TextFeld titel="Bezeichnung *" voll wert={f.name} aendern={setze("name")}
            platzhalter="z. B. Girokonto Mieten" />
          <WahlFeld titel="Art" wert={f.art} aendern={setze("art")} optionen={ausRecord(KONTOARTEN)} />
          <TextFeld titel="Bank" wert={f.bank} aendern={setze("bank")} />
          <ZahlFeld titel="Anfangsbestand (€)" wert={f.startsaldo} aendern={setze("startsaldo")} />
          <TextFeld titel="Stand vom" typ="date" wert={f.startsaldo_am}
            aendern={setze("startsaldo_am")} />
          <TextFeld titel="IBAN (nur zur Wiedererkennung)" voll wert={f.iban} aendern={setze("iban")} />
          <HakenFeld titel="Konto aktiv" wert={f.aktiv} aendern={setze("aktiv")} />
          <TextflaecheFeld titel="Notiz" wert={f.notiz} aendern={setze("notiz")} zeilen={2} />
        </div>
        <p className="hint">
          Der Kontostand ergibt sich aus dem Anfangsbestand plus allen Buchungen ab
          dem Stichtag. So musst du keine Historie nachtragen: Trag den heutigen
          Stand ein und buche ab hier weiter. Die Anwendung überweist nichts — die
          IBAN dient nur der Wiedererkennung.
        </p>
      </Sheet>
    </form>
  );
}

function Konten() {
  const bestand = useBestand();
  const [blatt, setzeBlatt] = useState<{ konto: Konto | null } | null>(null);
  const gesamt = bestand.konten.filter((k) => k.aktiv).reduce((s, k) => s + k.stand, 0);

  return (
    <>
      <div className="san-head">
        <div>
          Summe aktiver Konten:{" "}
          <b style={{ fontVariantNumeric: "tabular-nums", color: gesamt < 0 ? "var(--red)" : "var(--ink)" }}>
            {eur2(gesamt)}
          </b>
        </div>
        <button className="btn btn-primary btn-sm"
          onClick={() => setzeBlatt({ konto: null })}>+ Konto</button>
      </div>

      {bestand.konten.length === 0 ? (
        <Leer titel="Keine Konten.">
          Lege deine Konten an — Giro, Tagesgeld, Bar. Jede Buchung lässt sich
          dann einem Konto zuordnen, und der Stand ergibt sich von selbst.
        </Leer>
      ) : bestand.konten.map((k) => (
        <div className="row-card" key={k.id}>
          <div style={{ minWidth: 200 }}>
            <div className="row-title">
              {k.name}
              <span className="chip niedrig">{KONTOARTEN[k.art] ?? k.art}</span>
              {!k.aktiv ? <span className="chip hoch">stillgelegt</span> : null}
            </div>
            <div className="row-sub">
              {k.bank || "ohne Bank"} · Anfangsbestand {eur2(k.startsaldo)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <Mini titel="Bewegung" wert={eur2(k.bewegung)} />
            <Mini titel="Stand" wert={eur2(k.stand)} ton={k.stand < 0 ? "red" : "blue"} />
            <button className="btn btn-quiet btn-sm"
              onClick={() => setzeBlatt({ konto: k })}>Bearbeiten</button>
          </div>
        </div>
      ))}

      {blatt ? <KontoBlatt konto={blatt.konto} aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}

// ------------------------------------------------------------------- Darlehen

function DarlehenBlatt(
  { darlehen, aufSchliessen }: { darlehen: Darlehen | null; aufSchliessen: () => void },
) {
  const bestand = useBestand();
  const { aendern, zeigeMeldung } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);
  const [f, setzeF] = useState({
    bank: darlehen?.bank ?? "", nummer: darlehen?.nummer ?? "",
    betrag: darlehen ? String(darlehen.betrag) : "",
    zinssatz: darlehen ? String(darlehen.zinssatz) : "",
    rate_monatlich: darlehen ? String(darlehen.rate_monatlich) : "",
    beginn: darlehen?.beginn ?? "", zinsbindung_bis: darlehen?.zinsbindung_bis ?? "",
    restschuld: darlehen ? String(darlehen.restschuld) : "",
    restschuld_am: darlehen?.restschuld_am ?? "",
    objekt_id: darlehen?.objekt_id ?? "", aktiv: darlehen?.aktiv ?? true,
    notiz: darlehen?.notiz ?? "",
  });
  const setze = <K extends keyof typeof f>(k: K) => (w: typeof f[K]) => setzeF({ ...f, [k]: w });
  const zahl = (w: string) => {
    const n = Number(w.replace(",", "."));
    return Number.isFinite(n) ? Math.abs(n) : 0;
  };

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (!f.bank.trim()) return;
      const daten = {
        bank: f.bank.trim(), nummer: f.nummer, betrag: zahl(f.betrag),
        zinssatz: zahl(f.zinssatz), rate_monatlich: zahl(f.rate_monatlich),
        beginn: f.beginn || null, zinsbindung_bis: f.zinsbindung_bis || null,
        restschuld: f.restschuld === "" ? zahl(f.betrag) : zahl(f.restschuld),
        restschuld_am: f.restschuld_am || null,
        objekt_id: f.objekt_id || null, aktiv: f.aktiv, notiz: f.notiz,
      };
      setzeLaeuft(true);
      const ok = await aendern(
        () => darlehen ? api.darlehenAendern(darlehen.id, daten) : api.darlehenAnlegen(daten),
        "Gespeichert");
      setzeLaeuft(false);
      if (ok) aufSchliessen();
    }}>
      <Sheet
        titel={darlehen ? "Darlehen bearbeiten" : "Neues Darlehen"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {darlehen ? (
            <button className="btn btn-danger" type="button" onClick={async () => {
              if (!confirm("Dieses Darlehen löschen?")) return;
              try {
                await api.darlehenLoeschen(darlehen.id);
                zeigeMeldung("Darlehen gelöscht");
                aufSchliessen();
              } catch (fehler) {
                zeigeMeldung(fehler instanceof ApiFehler ? fehler.message : "Nicht möglich.", true);
              }
            }}>Löschen</button>
          ) : <span />}
          <div className="r">
            <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
            <button className="btn btn-primary" type="submit" disabled={laeuft}>
              {laeuft ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>}
      >
        <div className="form-grid">
          <TextFeld titel="Bank *" wert={f.bank} aendern={setze("bank")} platzhalter="z. B. Sparkasse" />
          <TextFeld titel="Darlehensnummer" wert={f.nummer} aendern={setze("nummer")} />
          <ZahlFeld titel="Darlehenssumme (€)" wert={f.betrag} aendern={setze("betrag")} />
          <ZahlFeld titel="Zinssatz (% p. a.)" wert={f.zinssatz} aendern={setze("zinssatz")}
            platzhalter="z. B. 3.6" />
          <ZahlFeld titel="Monatliche Rate (€)" wert={f.rate_monatlich}
            aendern={setze("rate_monatlich")} />
          <WahlFeld titel="Objekt" wert={f.objekt_id} aendern={setze("objekt_id")}
            optionen={[["", "— kein Objekt —"] as const,
              ...bestand.objekte.map((o) => [o.id, o.name] as const)]} />
          <ZahlFeld titel="Restschuld (€)" wert={f.restschuld} aendern={setze("restschuld")}
            platzhalter="leer = Darlehenssumme" />
          <TextFeld titel="Restschuld vom" typ="date" wert={f.restschuld_am}
            aendern={setze("restschuld_am")} />
          <TextFeld titel="Beginn" typ="date" wert={f.beginn} aendern={setze("beginn")} />
          <TextFeld titel="Zinsbindung bis" typ="date" wert={f.zinsbindung_bis}
            aendern={setze("zinsbindung_bis")} />
          <HakenFeld titel="Darlehen läuft" wert={f.aktiv} aendern={setze("aktiv")} />
          <TextflaecheFeld titel="Notiz" wert={f.notiz} aendern={setze("notiz")} zeilen={2} />
        </div>
        <p className="hint">
          Aus Restschuld, Zinssatz und Rate errechnet sich die Aufteilung jeder
          Rate in Zins und Tilgung. Nur der Zinsanteil ist absetzbar — deshalb
          werden beim Buchen einer Rate zwei getrennte Buchungen erzeugt.
        </p>
      </Sheet>
    </form>
  );
}

function Darlehen() {
  const bestand = useBestand();
  const { aendern, zeigeMeldung } = useZustand();
  const [blatt, setzeBlatt] = useState<{ darlehen: Darlehen | null } | null>(null);

  const restschuld = bestand.darlehen.filter((d) => d.aktiv).reduce((s, d) => s + d.restschuld, 0);
  const zinsProJahr = bestand.darlehen.filter((d) => d.aktiv).reduce((s, d) => s + d.zinsProJahr, 0);
  const rate = bestand.darlehen.filter((d) => d.aktiv).reduce((s, d) => s + d.rate_monatlich, 0);

  return (
    <>
      <div className="kpi-grid">
        <Kpi titel="Restschuld" wert={eur(restschuld)} unten="laufende Darlehen"
          ton={restschuld > 0 ? "red" : undefined} />
        <Kpi titel="Rate monatlich" wert={eur(rate)} unten="Zins und Tilgung zusammen" />
        <Kpi titel="Zinslast pro Jahr" wert={eur(zinsProJahr)} unten="beim heutigen Stand absetzbar" />
      </div>

      <div className="san-head">
        <div className="sub" style={{ margin: 0 }}>
          Jede gebuchte Rate schreibt die Restschuld fort — der Zinsanteil der
          nächsten Rate stimmt dadurch von selbst.
        </div>
        <button className="btn btn-primary btn-sm"
          onClick={() => setzeBlatt({ darlehen: null })}>+ Darlehen</button>
      </div>

      {bestand.darlehen.length === 0 ? (
        <Leer titel="Keine Darlehen.">
          Finanzierungen erfassen, damit Zins und Tilgung getrennt in die
          Auswertung laufen. Nur der Zins ist absetzbar.
        </Leer>
      ) : bestand.darlehen.map((d) => (
        <div className="row-card" key={d.id}>
          <div style={{ minWidth: 220 }}>
            <div className="row-title">
              {d.bank}{d.nummer ? ` · ${d.nummer}` : ""}
              {!d.aktiv ? <span className="chip done">getilgt</span> : null}
              {d.objekt_id
                ? <span className="chip niedrig">
                    {bestand.objekte.find((o) => o.id === d.objekt_id)?.name ?? ""}
                  </span>
                : null}
            </div>
            <div className="row-sub">
              {pct(d.zinssatz / 100)} · Rate {eur2(d.rate_monatlich)}
              {d.zinsbindung_bis ? ` · Zinsbindung bis ${alsDatum(d.zinsbindung_bis)}` : ""}
              {d.restschuld_am ? ` · Stand ${alsDatum(d.restschuld_am)}` : ""}
            </div>
            {d.aktiv && d.naechsteRate.rate > 0 ? (
              <div className="row-sub" style={{ marginTop: 4 }}>
                Nächste Rate: <b>{eur2(d.naechsteRate.zins)} Zins</b> (absetzbar)
                {" + "}{eur2(d.naechsteRate.tilgung)} Tilgung
                {d.naechsteRate.letzteRate ? " — letzte Rate" : ""}
              </div>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <Mini titel="Restschuld" wert={eur(d.restschuld)} ton={d.restschuld > 0 ? "red" : undefined} />
            <div className="row-actions">
              {d.aktiv && d.naechsteRate.rate > 0 ? (
                <button className="btn btn-quiet btn-sm" onClick={async () => {
                  const konto = bestand.konten.find((k) => k.aktiv);
                  if (!confirm(`Rate buchen?\n\n`
                    + `Zins ${eur2(d.naechsteRate.zins)} (absetzbar)\n`
                    + `Tilgung ${eur2(d.naechsteRate.tilgung)}\n\n`
                    + `Es entstehen zwei Buchungen${konto ? ` auf „${konto.name}“` : ""}.`)) return;
                  let text = "";
                  const ok = await aendern(async () => {
                    const ergebnis: Portfolio = await api.rateBuchen(d.id,
                      { datum: heute(), konto_id: konto?.id ?? null });
                    const r = ergebnis.rate;
                    text = r
                      ? `Gebucht: ${eur2(r.zins)} Zins, ${eur2(r.tilgung)} Tilgung · `
                        + `Restschuld ${eur(r.neueRestschuld)}`
                      : "";
                    return ergebnis;
                  });
                  if (ok && text) zeigeMeldung(text);
                }}>Rate buchen</button>
              ) : null}
              <button className="btn btn-quiet btn-sm"
                onClick={() => setzeBlatt({ darlehen: d })}>Bearbeiten</button>
            </div>
          </div>
        </div>
      ))}

      {blatt ? <DarlehenBlatt darlehen={blatt.darlehen}
        aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}

// ------------------------------------------------------------------ Übersicht

function Uebersicht() {
  const bestand = useBestand();
  const { zeigeMeldung } = useZustand();
  const [jahr, setzeJahr] = useState(new Date().getFullYear());
  const [zahlen, setzeZahlen] = useState<Jahreszahlen | null>(bestand.finanzjahr);
  const [kategorien, setzeKategorien] = useState<KategorieSumme[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const daten = await api.finanzjahr(jahr);
        setzeZahlen(daten.zahlen);
        setzeKategorien(daten.kategorien);
      } catch {
        zeigeMeldung("Die Jahreszahlen ließen sich nicht laden.", true);
      }
    })();
  }, [jahr, zeigeMeldung, bestand.buchungen.length]);

  const kontostand = bestand.konten.filter((k) => k.aktiv).reduce((s, k) => s + k.stand, 0);
  const restschuld = bestand.darlehen.filter((d) => d.aktiv).reduce((s, d) => s + d.restschuld, 0);
  const z = zahlen;
  const jahre = [jahr - 2, jahr - 1, new Date().getFullYear()].filter(
    (j, i, a) => a.indexOf(j) === i).sort();

  const ausgaben = kategorien.filter((k) => k.art === "ausgabe").sort((a, b) => b.summe - a.summe);
  const einnahmen = kategorien.filter((k) => k.art === "einnahme").sort((a, b) => b.summe - a.summe);
  const groesste = Math.max(1, ...ausgaben.map((k) => k.summe));

  return (
    <>
      <div className="san-head">
        <div className="seg" style={{ margin: 0 }}>
          {jahre.map((j) => (
            <button key={j} className={jahr === j ? "on" : ""} onClick={() => setzeJahr(j)}>{j}</button>
          ))}
        </div>
        <span className="sub" style={{ margin: 0 }}>
          {z && z.verbleibendeMonate > 0
            ? `${z.verbleibendeMonate} Monate offen — Prognose enthält Schätzungen`
            : "abgeschlossenes Jahr"}
        </span>
      </div>

      <div className="more-sec">Aktueller Stand</div>
      <div className="kpi-grid">
        <Kpi titel="Kontostand" wert={eur2(kontostand)} unten="Summe aktiver Konten"
          ton={kontostand < 0 ? "red" : "blue"} />
        <Kpi titel="Restschuld" wert={eur(restschuld)} unten="laufende Darlehen"
          ton={restschuld > 0 ? "red" : undefined} />
        <Kpi titel="Vermögensposition" wert={eur(kontostand - restschuld)}
          unten="Konten minus Restschuld"
          ton={kontostand - restschuld < 0 ? "red" : undefined} />
      </div>

      {z ? (
        <>
          <div className="more-sec">Jahr {z.jahr} — bereits gebucht</div>
          <div className="kpi-grid">
            <Kpi titel="Einnahmen" wert={eur(z.einnahmenIst)} unten="alle Eingänge" />
            <Kpi titel="Ausgaben laufend" wert={eur(z.ausgabenLaufendIst)}
              unten="Betrieb, Erhaltung, Verwaltung" />
            <Kpi titel="Investitionen" wert={eur(z.ausgabenInvestivIst)}
              unten="wirken über die AfA" />
            <Kpi titel="Zinsen" wert={eur(z.zinsenIst)} unten="absetzbar" />
            <Kpi titel="Tilgung" wert={eur(z.tilgungIst)} unten="kein Aufwand, nur Cashflow" />
            <Kpi titel="Überschuss" wert={eur(z.ueberschussIst)}
              unten="ohne Tilgung gerechnet"
              ton={z.ueberschussIst < 0 ? "red" : "blue"} />
          </div>

          <div className="more-sec">Jahresprognose {z.jahr}</div>
          <div className="kpi-grid">
            <Kpi titel="Einnahmen erwartet" wert={eur(z.einnahmenPrognose)}
              unten="Ist plus laufende Mietverträge" />
            <Kpi titel="Ausgaben erwartet" wert={eur(z.ausgabenPrognose)}
              unten="Ist plus Darlehen und Hochrechnung" />
            <Kpi titel="Überschuss erwartet" wert={eur(z.ueberschussPrognose)}
              ton={z.ueberschussPrognose < 0 ? "red" : "blue"} unten="ohne Tilgung" />
            <Kpi titel="Cashflow erwartet" wert={eur(z.cashflowPrognose)}
              ton={z.cashflowPrognose < 0 ? "red" : undefined}
              unten="was tatsächlich vom Konto geht" />
          </div>

          <p className="sub">
            <b>Wie die Prognose entsteht:</b> Mieteinnahmen und Darlehensraten sind
            keine Schätzung — sie stehen in den Verträgen und werden für die
            verbleibenden {z.verbleibendeMonate} Monate hochgerechnet. Alle übrigen
            laufenden Kosten werden aus dem bisherigen Jahresverlauf fortgeschrieben;
            das ist eine echte Schätzung. Investitionen werden bewusst <b>nicht</b>{" "}
            hochgerechnet — eine einmalige Küche im Februar würde sonst auf zwölf
            Monate vervielfacht.
          </p>

          <div className="more-sec">Steuerlich</div>
          <div className="kpi-grid">
            <Kpi titel="Absetzbares Ergebnis" wert={eur(z.absetzbarIst)}
              unten="Einnahmen minus absetzbare Ausgaben"
              ton={z.absetzbarIst < 0 ? "red" : undefined} />
          </div>
          <p className="sub">
            Rechenhilfe für die Steuerberatung, keine Steuererklärung. Die AfA auf
            Anschaffungen ist hier nicht enthalten — sie hängt von Gebäudeanteil und
            Abschreibungssatz ab und gehört in fachkundige Hände.
          </p>
        </>
      ) : null}

      {ausgaben.length > 0 ? (
        <>
          <div className="more-sec">Ausgaben nach Kategorie {jahr}</div>
          <div className="table">
            {ausgaben.map((k) => (
              <div className="trow kat-ausgabe" key={k.id}>
                <div>
                  <div style={{ fontWeight: 600 }}>{k.name}</div>
                  <div className="row-sub">
                    {anzahl(k.anzahl, "Buchung", "Buchungen")}
                    {k.typ !== "laufend" ? ` · ${TYP_TEXT[k.typ]}` : ""}
                    {k.umlagefaehig ? " · umlagefähig" : ""}
                  </div>
                </div>
                <div className="hide-m" style={{ alignSelf: "center" }}>
                  <div className="san-bar" style={{ marginBottom: 0 }}>
                    <div style={{
                      width: `${(k.summe / groesste) * 100}%`,
                      background: k.typ === "investiv" ? "var(--orange)"
                        : k.typ === "finanzierung" ? "var(--muted)" : "var(--blue)",
                    }} />
                  </div>
                </div>
                <span className="num">{eur(k.summe)}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {einnahmen.length > 0 ? (
        <>
          <div className="more-sec">Einnahmen nach Kategorie {jahr}</div>
          <div className="table">
            {einnahmen.map((k) => (
              <div className="trow kat-einnahme" key={k.id}>
                <div>
                  <div style={{ fontWeight: 600 }}>{k.name}</div>
                  <div className="row-sub">{anzahl(k.anzahl, "Buchung", "Buchungen")}</div>
                </div>
                <span className="num" style={{ color: "var(--green-ink)" }}>{eur(k.summe)}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {bestand.buchungen.length === 0 ? (
        <Leer titel="Noch nichts gebucht.">
          Ohne Buchungen bleibt hier alles bei null. Fang mit den Konten an, trag
          den heutigen Stand ein und buche ab da weiter.
        </Leer>
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------- Die Seite

export function FinanzenSeite({ bereich }: { bereich?: string }) {
  const { gehe } = useZustand();
  const bestand = useBestand();
  const aktiv = bereich ?? "uebersicht";

  const kontostand = bestand.konten.filter((k) => k.aktiv).reduce((s, k) => s + k.stand, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Finanzen</h1>
          <p className="sub">
            {anzahl(bestand.buchungen.length, "Buchung", "Buchungen")} ·{" "}
            {anzahl(bestand.konten.length, "Konto", "Konten")} · Stand {eur2(kontostand)}
          </p>
        </div>
      </div>

      <div className="seg">
        {reiter.map(([schluessel, text]) => (
          <button key={schluessel} className={aktiv === schluessel ? "on" : ""}
            onClick={() => gehe({ seite: "finanzen", bereich: schluessel })}>{text}</button>
        ))}
      </div>

      {aktiv === "uebersicht" ? <Uebersicht /> : null}
      {aktiv === "buchungen" ? <Buchungen /> : null}
      {aktiv === "konten" ? <Konten /> : null}
      {aktiv === "darlehen" ? <Darlehen /> : null}
    </>
  );
}
